<script lang="ts">
	// A5: how a suspended restaurant reaches Bosla (UC-22). The email follows
	// the domain and only shows here; the WhatsApp number is typed with its
	// country code, and an empty one removes it. Beside the form, what a
	// suspended owner and a suspended till read with this contact, as typed,
	// so a typo shows before anyone needs the number.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Field from '../components/Field.svelte';
	import Loading from '../components/Loading.svelte';
	import { api } from '../api/client';
	import type { components } from '../api/types';
	import { copy } from './copy';
	import { flash } from './flash.svelte';
	import { instantDay } from './format';
	import { sessionEnded } from './session.svelte';

	type Contact = components['schemas']['AdminSupportContactBody'];

	// The server's rule, checked here first so a typo answers at once: spaces
	// go, then empty or a plus sign and 8 to 15 digits.
	const WHATSAPP = /^\+\d{8,15}$/;

	let contact = $state<Contact | null>(null);
	let draft = $state('');
	let loadFailed = $state(false);
	let invalid = $state(false);
	let saveFailed = $state(false);
	let saving = $state(false);

	const typed = $derived(draft.replace(/\s+/g, ''));
	const unchanged = $derived(contact !== null && typed === contact.whatsapp && !saveFailed);

	async function load() {
		loadFailed = false;
		try {
			const { data, response } = await api.GET('/api/admin/support-contact');
			if (response.status === 401) {
				sessionEnded();
				return;
			}
			if (!data) {
				loadFailed = true;
				return;
			}
			contact = data;
			draft = data.whatsapp;
		} catch {
			loadFailed = true;
		}
	}

	$effect(() => {
		void load();
	});

	async function save() {
		if (saving) return;
		if (typed !== '' && !WHATSAPP.test(typed)) {
			invalid = true;
			return;
		}
		saving = true;
		saveFailed = false;
		// What was sent, so an edit typed while it travels is kept.
		const sent = typed;
		try {
			const { data, error, response } = await api.PUT('/api/admin/support-contact', {
				body: { whatsapp: sent },
			});
			if (response.status === 401) {
				sessionEnded();
				return;
			}
			if (error?.code === 'PHONE_INVALID') {
				invalid = true;
				return;
			}
			if (!data) {
				saveFailed = true;
				return;
			}
			contact = data;
			if (typed === sent) draft = data.whatsapp;
			flash(copy.saved);
		} catch {
			saveFailed = true;
		} finally {
			saving = false;
		}
	}
</script>

<div class="contact" data-testid="admin-support">
	{#if loadFailed}
		<Band
			tone="flag"
			action={copy.retry}
			onAction={() => void load()}
			testId="admin-support-failed"
		>
			{copy.contactFailed}
		</Band>
	{:else if !contact}
		<div class="wait">
			<Loading label={copy.contactLoading} caption={copy.contactLoading} size="lg" />
		</div>
	{:else}
		<div class="cards">
			<Card title={copy.contactCard} pad testId="admin-support-form">
				<div class="form">
					<Field
						testId="admin-support-email"
						label={copy.email}
						value={contact.email}
						mono
						note={copy.emailNote}
					/>
					<Field
						testId="admin-support-whatsapp"
						label={copy.whatsapp}
						value={draft}
						mono
						type="tel"
						inputMode="tel"
						maxLength={24}
						placeholder="+201001234567"
						note={copy.whatsappNote}
						error={invalid ? copy.whatsappInvalid : undefined}
						errorTestId="admin-support-invalid"
						onInput={(value) => {
							draft = value;
							invalid = false;
							saveFailed = false;
						}}
					/>
					<div class="facts">
						{#if !contact.whatsapp}
							<p class="none" data-testid="admin-support-none">{copy.noWhatsapp}</p>
						{/if}
						{#if contact.changedAt && contact.changedBy}
							<p class="changed" data-testid="admin-support-changed">
								{copy.changed(instantDay(contact.changedAt), contact.changedBy)}
							</p>
						{/if}
					</div>
					{#if saveFailed}
						<Band tone="flag" testId="admin-support-save-failed">{copy.saveFailed}</Band>
					{/if}
					<div class="actions">
						<Button
							testId="admin-support-save"
							label={copy.save}
							tone="primary"
							size="md"
							disabled={unchanged}
							state={saving ? 'pending' : 'idle'}
							pendingLabel={copy.saving}
							onPress={() => void save()}
						/>
					</div>
				</div>
			</Card>

			<Card title={copy.previewCard} note={copy.previewNote} pad testId="admin-support-preview">
				<div class="form">
					<div class="sample">
						<span class="caption">{copy.previewOwner}</span>
						<div class="message">
							<p class="title">{copy.previewOwnerTitle}</p>
							<p class="line">{copy.previewOwnerTills}</p>
							<div class="reach">
								<span class="line">{copy.previewOwnerContact}</span>
								<span class="mono link">{contact.email}</span>
								{#if typed}
									<span class="wa link">
										{copy.whatsappLine} <bdi dir="ltr" class="mono">{typed}</bdi>
									</span>
								{/if}
							</div>
						</div>
					</div>
					<div class="sample">
						<span class="caption">{copy.previewTill}</span>
						<div class="message">
							<p class="title">{copy.previewTillTitle}</p>
							<div class="reach">
								<span class="line">{copy.previewTillContact}</span>
								<span class="mono strong">{contact.email}</span>
								{#if typed}
									<span class="wa" data-testid="admin-support-preview-whatsapp">
										{copy.whatsappLine} <bdi dir="ltr" class="mono strong">{typed}</bdi>
									</span>
								{/if}
							</div>
						</div>
					</div>
				</div>
			</Card>
		</div>
	{/if}
</div>

<style>
	.contact {
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.wait {
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}

	.cards {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 360px), 1fr));
		gap: var(--grid-gap);
		align-items: start;
	}

	.form {
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.facts {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.none {
		margin: 0;
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.changed {
		margin: 0;
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.actions {
		display: flex;
	}

	.sample {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	.caption {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.message {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
		padding: var(--space-7);
		background: var(--surface-page);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-card);
	}

	.title {
		margin: 0;
		font-size: var(--text-base);
		font-weight: 700;
		line-height: var(--leading-snug);
	}

	.line {
		margin: 0;
		font-size: var(--text-sm);
		color: var(--text-body);
	}

	.reach {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-1);
	}

	.mono {
		font-family: var(--font-mono);
		font-size: var(--text-sm);
		font-weight: 600;
	}

	.wa {
		font-size: var(--text-sm);
		font-weight: 600;
	}

	.link {
		color: var(--link);
	}

	.strong {
		font-weight: 700;
	}
</style>
